import React, { useEffect, useState } from "react";
import moment from "moment";
import Swal from "sweetalert2";
import { updateCommissionInvoiceExpectedPayment } from "../commissionsApi";

function toDateInputValue(value) {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function CommissionInvoiceExpectedPaymentEditor({
  invoiceId,
  providerName = "",
  expectedPaymentDate = null,
  expectedPaymentNote = "",
  onSaved,
  onCancel,
  compact = false,
  hideCancel = false,
  inline = false,
  saveExpectedPayment = updateCommissionInvoiceExpectedPayment,
}) {
  const [date, setDate] = useState(toDateInputValue(expectedPaymentDate));
  const [note, setNote] = useState(expectedPaymentNote || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDate(toDateInputValue(expectedPaymentDate));
    setNote(expectedPaymentNote || "");
  }, [expectedPaymentDate, expectedPaymentNote, invoiceId]);

  const handleSave = async () => {
    if (!invoiceId) return;

    const trimmedNote = String(note || "").trim();
    const hadSavedDate = Boolean(expectedPaymentDate);
    const isClearingDate = !date && hadSavedDate;

    if (!date && !isClearingDate) {
      Swal.fire("Required", "Please select an expected payment date.", "warning");
      return;
    }

    setSaving(true);
    try {
      const updated = await saveExpectedPayment(invoiceId, {
        expected_payment_date: date || null,
        expected_payment_note: trimmedNote,
      });
      onSaved?.({
        expectedPaymentDate:
          updated?.expectedPaymentDate ??
          updated?.raw?.expected_payment_date ??
          (date || null),
        expectedPaymentNote:
          updated?.expectedPaymentNote ??
          updated?.raw?.expected_payment_note ??
          trimmedNote,
      });
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to save expected payment details.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDate = () => {
    setDate("");
  };

  const showProvider = !inline && providerName;

  return (
    <div
      className={`cms-invoice-expected-payment-editor${
        compact ? " cms-invoice-expected-payment-editor--compact" : ""
      }${inline ? " cms-invoice-expected-payment-editor--inline" : ""}`}
    >
      <div className="cms-invoice-expected-payment-editor__grid">
        {showProvider ? (
          <div className="cms-invoice-expected-payment-editor__provider">
            <span className="cms-invoice-expected-payment-editor__label">
              Provider
            </span>
            <p className="cms-invoice-expected-payment-editor__provider-name">
              {providerName}
            </p>
          </div>
        ) : null}

        <div className="cms-invoice-expected-payment-editor__field cms-invoice-expected-payment-editor__field--note">
          <label
            className="cms-invoice-expected-payment-editor__label"
            htmlFor={`expected-note-${invoiceId}`}
          >
            Note
          </label>
          <textarea
            id={`expected-note-${invoiceId}`}
            className="form-control cms-invoice-expected-payment-editor__textarea"
            rows={inline ? 2 : compact ? 2 : 3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Payment context or follow-up details…"
            disabled={saving}
          />
        </div>

        <div className="cms-invoice-expected-payment-editor__field cms-invoice-expected-payment-editor__field--date">
          <div className="cms-invoice-expected-payment-editor__field-header">
            <label
              className="cms-invoice-expected-payment-editor__label"
              htmlFor={`expected-date-${invoiceId}`}
            >
              Expected date
            </label>
            {date ? (
              <button
                type="button"
                className="cms-invoice-expected-payment-editor__remove"
                onClick={handleRemoveDate}
                disabled={saving}
              >
                Remove date
              </button>
            ) : null}
          </div>
          <input
            id={`expected-date-${invoiceId}`}
            type="date"
            className="form-control cms-invoice-expected-payment-editor__date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            disabled={saving}
          />
        </div>

        <div className="cms-invoice-expected-payment-editor__actions">
          {!hideCancel ? (
            <button
              type="button"
              className="btn btn-light cms-invoice-expected-payment-editor__btn cms-invoice-expected-payment-editor__btn--cancel"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary cms-invoice-expected-payment-editor__btn cms-invoice-expected-payment-editor__btn--save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CommissionInvoiceExpectedPaymentEditor;
